import React, { useState, useEffect } from 'react';
import { Box, Typography, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import ModuleHeader from './components/ModuleHeader';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import axios from 'axios';
import { API_URL } from './config';
import { useAuth } from './AuthContext';
import { formatarSE } from './utils/semanaEpi';

interface Props { onVoltar: () => void; }

interface Ciclo   { id: number; nome: string; ativo: boolean; }
interface AreaInfo { id: number; nome: string; }
interface Agente  { id: number; nome: string; }

interface RankingItem {
  agente_id: number;
  nome: string;
  imoveis_trabalhados: number;
  taxa_recuperacao_pct: number;
  saldo_pendente: number;
}

interface HistoricoItem {
  ano: number;
  se: number;
  imoveis: number;
}

function corAlerta(texto: string) {
  if (texto.startsWith("🔴")) return { bg: "#FFCDD2", border: "#C62828", color: "#B71C1C" };
  if (texto.startsWith("⚠️"))  return { bg: "#FFF8E1", border: "#F57F17", color: "#E65100" };
  if (texto.startsWith("🟡")) return { bg: "#FFFDE7", border: "#FBC02D", color: "#F57F17" };
  if (texto.startsWith("🟢")) return { bg: "#C8E6C9", border: "#2E7D32", color: "#1B5E20" };
  return { bg: "#F5F5F5", border: "#9E9E9E", color: "#424242" };
}

const CARDS = [
  { label: 'Imóveis Trabalhados', campo: 'imoveis_trabalhados', bg: '#E3F2FD', cor: '#1565C0' },
  { label: 'Pendências Geradas',  campo: 'pendencias_geradas',  bg: '#FFF3E0', cor: '#E65100' },
  { label: 'Recuperados',         campo: 'recuperados',          bg: '#E8F5E9', cor: '#2E7D32' },
  { label: 'Saldo Pendente',      campo: 'saldo_pendente',       bg: '#FFE4E1', cor: '#C62828' },
  { label: 'Taxa de Recuperação', campo: '__taxa__',             bg: '#F3E5F5', cor: '#6A1B9A' },
  { label: 'Dias Trabalhados',    campo: 'dias_trabalhados',     bg: '#F5F5F5', cor: '#1D1D1F' },
  { label: 'Densidade Depósitos', campo: 'densidade_depositos',  bg: '#FFF9C4', cor: '#F57F17' },
  { label: 'Taxa Positividade',   campo: '__positividade__',      bg: '#FFEBEE', cor: '#B71C1C' },
  { label: 'Média Diária',        campo: 'media_diaria_imoveis',  bg: '#E0F2F1', cor: '#00695C' },
];

export default function PainelAgente({ onVoltar }: Props) {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [ciclos,  setCiclos]  = useState<Ciclo[]>([]);
  const [areas,   setAreas]   = useState<AreaInfo[]>([]);
  const [agentes, setAgentes] = useState<Agente[]>([]);

  const [filtroCiclo,  setFiltroCiclo]  = useState<string>('');
  const [filtroSE,     setFiltroSE]     = useState<string>('');
  const [filtroArea,   setFiltroArea]   = useState<string>('');
  const [filtroAgente, setFiltroAgente] = useState<string>('');

  const [dados, setDados] = useState<any>(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API_URL}/api/ciclos/`,  { headers }),
      axios.get(`${API_URL}/api/areas/`,   { headers }),
      axios.get(`${API_URL}/api/agentes/`, { headers }),
    ]).then(([rc, ra, rag]) => {
      setCiclos(rc.data);
      setAreas(ra.data);
      setAgentes(rag.data);
      const ativo = rc.data.find((c: Ciclo) => c.ativo);
      if (ativo) setFiltroCiclo(String(ativo.id));
    });
  }, [token]); // eslint-disable-line

  useEffect(() => {
    const params: Record<string, string> = {};
    if (filtroCiclo)  params.ciclo_id  = filtroCiclo;
    if (filtroSE)     params.se        = filtroSE;
    if (filtroArea)   params.area_id   = filtroArea;
    if (filtroAgente) params.agente_id = filtroAgente;

    axios.get(`${API_URL}/api/painel-agente/dados`, { headers, params })
      .then(r => setDados(r.data))
      .catch(() => {});
  }, [filtroCiclo, filtroSE, filtroArea, filtroAgente, token]); // eslint-disable-line

  const cards = dados?.cards;
  const ranking: RankingItem[] = dados?.ranking ?? [];
  const ficha = dados?.ficha ?? null;
  const maxImoveis = ranking.length > 0 ? ranking[0].imoveis_trabalhados : 1;

  function valorCard(campo: string): string {
    if (!cards) return '-';
    if (campo === '__taxa__')
      return `${cards.recuperados} (${cards.taxa_recuperacao_pct}%)`;
    if (campo === '__positividade__')
      return `${cards.positivos_total} (${cards.taxa_positividade_pct}%)`;
    return String(cards[campo] ?? '-');
  }

  const historico: HistoricoItem[] = ficha?.historico_8se ?? [];
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F2F2F7' }}>
      <ModuleHeader
        titulo="Painel Agente"
        subtitulo="Indicadores e ranking por agente"
        icone={<PersonIcon sx={{ fontSize: 32 }} />}
        accentColor="#1B3A6B"
        onVoltar={onVoltar}
        chips={dados?.cards ? [
          { label: 'Trabalhados', value: dados.cards.imoveis_trabalhados ?? 0 },
          { label: 'Pendentes',   value: dados.cards.saldo_pendente       ?? 0 },
          { label: 'Recup',       value: `${dados.cards.recuperados ?? 0} (${dados.cards.taxa_recuperacao_pct ?? 0}%)` },
        ] : undefined}
      />

      {/* Filtros */}
      <Box sx={{ display: 'flex', gap: 2, p: 2, backgroundColor: '#fff', borderBottom: '1px solid #E5E7EB', flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Ciclo</InputLabel>
          <Select value={filtroCiclo} label="Ciclo" onChange={e => setFiltroCiclo(e.target.value)}>
            <MenuItem value="">Todos</MenuItem>
            {ciclos.map(c => <MenuItem key={c.id} value={String(c.id)}>{c.nome}{c.ativo ? ' ●' : ''}</MenuItem>)}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel>SE</InputLabel>
          <Select value={filtroSE} label="SE" onChange={e => setFiltroSE(e.target.value)}>
            <MenuItem value="">Todas</MenuItem>
            {Array.from({ length: 52 }, (_, i) => i + 1).map(se => (
              <MenuItem key={se} value={String(se)}>{se}ª SE</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Área</InputLabel>
          <Select value={filtroArea} label="Área" onChange={e => setFiltroArea(e.target.value)}>
            <MenuItem value="">Todas</MenuItem>
            {areas.map(a => <MenuItem key={a.id} value={String(a.id)}>{a.nome}</MenuItem>)}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Agente</InputLabel>
          <Select value={filtroAgente} label="Agente" onChange={e => setFiltroAgente(e.target.value)}>
            <MenuItem value="">Todos</MenuItem>
            {agentes.map(a => <MenuItem key={a.id} value={String(a.id)}>{a.nome}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>

      {filtroSE && (
        <Typography sx={{ mb: 2, fontWeight: 600, fontSize: 14, color: '#1D1D1F', px: 2 }}>
          {formatarSE(Number(filtroSE))}
        </Typography>
      )}

      {/* Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(9, 1fr)', gap: 2, p: 2 }}>
        {CARDS.map(({ label, campo, bg, cor }) => (
          <Box key={campo} sx={{ bgcolor: bg, borderRadius: 2, p: 2 }}>
            <Typography variant="caption" sx={{ color: cor, opacity: 0.75, display: 'block' }}>
              {label}
            </Typography>
            <Typography variant="h4" sx={{ color: cor, fontWeight: 700, mt: 0.5 }}>
              {valorCard(campo)}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Ranking */}
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Ranking de Agentes</Typography>
        {ranking.map((item, idx) => {
          const barPct = maxImoveis > 0 ? (item.imoveis_trabalhados / maxImoveis) * 100 : 0;
          return (
            <Box key={item.agente_id} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, p: 1, borderRadius: 1, bgcolor: '#fafafa' }}>
              <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 30 }}>
                #{idx + 1}
              </Typography>

              <Typography
                variant="body2"
                onClick={() => setFiltroAgente(String(item.agente_id))}
                sx={{ minWidth: 140, cursor: 'pointer', '&:hover': { textDecoration: 'underline', color: '#1565C0' } }}
              >
                {item.nome}
              </Typography>

              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ flex: 1, bgcolor: '#E3F2FD', borderRadius: 1, height: 24, overflow: 'hidden' }}>
                  <Box sx={{ width: `${barPct}%`, height: 24, bgcolor: '#1565C0', borderRadius: 1 }} />
                </Box>
                <Typography variant="body2" sx={{ minWidth: 36, textAlign: 'right', fontWeight: 600 }}>
                  {item.imoveis_trabalhados}
                </Typography>
              </Box>

              <Box sx={{ bgcolor: '#E8F5E9', color: '#2E7D32', px: 0.5, py: 0.5, borderRadius: 1, fontSize: '0.75rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
                {item.taxa_recuperacao_pct}%
              </Box>

              <Box sx={{ bgcolor: '#FFE4E1', color: '#C62828', px: 0.5, py: 0.5, borderRadius: 1, fontSize: '0.75rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
                {item.saldo_pendente} pend.
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Ficha do Agente */}
      {ficha && (
        <Box sx={{ p: 2, mt: 2, bgcolor: '#fff', borderRadius: 2, mx: 2, mb: 2 }}>
          <Box sx={{
            background: "linear-gradient(135deg, #1565C0 0%, #1B5E20 100%)",
            color: "#fff", p: 3, borderRadius: 2, mb: 3,
            display: "flex", alignItems: "center", justifyContent: "space-between",
          }}>
            <Typography variant="h4" sx={{ fontWeight: 800 }}>
              {agentes.find(a => String(a.id) === filtroAgente)?.nome ?? "Agente"}
            </Typography>
            <Box sx={{ bgcolor: "#fff", color: "#1565C0", p: 1, borderRadius: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {(() => {
                  const idx = dados?.ranking?.findIndex((r: any) => String(r.agente_id) === filtroAgente);
                  return idx !== undefined && idx >= 0 ? `#${idx + 1}` : "—";
                })()}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 2, mb: 3 }}>
            <Box sx={{ bgcolor: "#E3F2FD", border: "2px solid #1565C0", borderRadius: 2, p: 2.5, textAlign: "center" }}>
              <Typography variant="caption" sx={{ color: "#1565C0", fontWeight: 700, display: "block" }}>DIAS TRABALHADOS</Typography>
              <Typography variant="h3" sx={{ fontWeight: 800, color: "#0D47A1" }}>{cards?.dias_trabalhados ?? "-"}</Typography>
            </Box>
            {(() => {
              const atividadesEntries = Object.entries(ficha.atividades_dias || {}) as [string, number][];
              const atividadePrincipal = atividadesEntries.sort((a, b) => b[1] - a[1])[0] || ["—", 0];
              return (
                <Box sx={{ bgcolor: "#FFF3E0", border: "2px solid #E65100", borderRadius: 2, p: 2.5, textAlign: "center" }}>
                  <Typography variant="caption" sx={{ color: "#E65100", fontWeight: 700, display: "block" }}>ATIVIDADE PRINCIPAL</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: "#BF360C" }}>{atividadePrincipal[0]}</Typography>
                  <Typography variant="caption" sx={{ color: "#E65100", display: "block" }}>{atividadePrincipal[1]} dias</Typography>
                </Box>
              );
            })()}
            <Box sx={{ bgcolor: "#E8F5E9", border: "2px solid #2E7D32", borderRadius: 2, p: 2.5, textAlign: "center" }}>
              {ficha.dia_mais_produtivo ? (
                <>
                  <Typography variant="caption" sx={{ color: "#2E7D32", fontWeight: 700, display: "block" }}>🏆 DIA MAIS PRODUTIVO</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: "#1B5E20" }}>{ficha.dia_mais_produtivo.dia}</Typography>
                  <Typography variant="caption" sx={{ color: "#2E7D32", display: "block" }}>{ficha.dia_mais_produtivo.imoveis} imóveis</Typography>
                </>
              ) : (
                <Typography variant="h4" sx={{ fontWeight: 800, color: "#2E7D32" }}>—</Typography>
              )}
            </Box>
          </Box>

          {/* Alertas */}
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Alertas e Destaques</Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 3 }}>
            {(ficha.alertas as string[]).length === 0 ? (
              <Box sx={{ bgcolor: "#F5F5F5", p: 1.5, borderRadius: 1 }}>
                <Typography variant="body2">Nenhum alerta no momento</Typography>
              </Box>
            ) : (
              (ficha.alertas as string[]).map((alerta, i) => {
                const c = corAlerta(alerta);
                return (
                  <Box key={i} sx={{ p: 1.5, borderRadius: 1, bgcolor: c.bg, borderLeft: `4px solid ${c.border}` }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: c.color }}>{alerta}</Typography>
                  </Box>
                );
              })
            )}
          </Box>

          {/* Histórico */}
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Histórico de Produção (últimas SEs)</Typography>
          <Box sx={{ height: 180, mb: 3, bgcolor: "#fafafa", borderRadius: 1, p: 1 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={historico}>
                <defs>
                  <linearGradient id="colorImoveis" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#1565C0" stopOpacity={0.8} />
                    <stop offset="100%" stopColor="#1565C0" stopOpacity={0.1} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis dataKey="se" tickFormatter={(v) => `SE ${v}`} />
                <YAxis />
                <Tooltip formatter={(v: any) => [v, "Imóveis"]} labelFormatter={(v) => `SE ${v}`} />
                <Area type="monotone" dataKey="imoveis" stroke="#1565C0" fill="url(#colorImoveis)" />
              </AreaChart>
            </ResponsiveContainer>
          </Box>
            {/* Depositos por Tipo */}
          <Typography variant="subtitle2" sx={{ mt: 2 }}>Depósitos por Tipo</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 1, mt: 1 }}>
            {(["A1","A2","B","C","D1","D2","E"] as const).map(tipo => (
              <Box key={tipo} sx={{ p: 1, borderRadius: 1, bgcolor: "#F5F5F5", textAlign: "center" }}>
                <Typography variant="caption" sx={{ fontWeight: 700, display: "block" }}>{tipo}</Typography>
                <Typography variant="h6">{ficha.depositos_por_tipo?.[tipo] ?? 0}</Typography>
              </Box>
            ))}
          </Box>

          {/* Heatmap Quarteiroes */}
          <Typography variant="subtitle2" sx={{ mt: 2 }}>Heatmap de Quarteirões</Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
            {((ficha.heatmap_quarteiroes ?? []) as { quarteirao: string; visitas: number }[]).map((item) => {
              const bg = item.visitas >= 4 ? "#1565C0" : item.visitas === 3 ? "#42A5F5" : item.visitas === 2 ? "#90CAF9" : "#E3F2FD";
              const color = item.visitas >= 3 ? "#fff" : "#0D47A1";
              return (
                <Box key={item.quarteirao} sx={{ display: "inline-flex", alignItems: "center", p: 1, borderRadius: 1, bgcolor: bg }}>
                  <Typography sx={{ fontWeight: 700, fontSize: "0.78rem", color }}>
                    {item.quarteirao}
                  </Typography>
                  <Typography sx={{ fontSize: "0.72rem", color, ml: 0.4 }}>
                    ({item.visitas})
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </Box>
  );
}
